// Admin Products Component (Products.jsx) - Updated with dynamic colors

import { useState, useEffect, useCallback, useRef } from "react";
import { aGet, aPost, aPut, aDelete } from "../../utils/adminApi";
import {
  C,
  Input,
  Select,
  Btn,
  Table,
  Pagination,
  Modal,
  Badge,
  EmptyState,
  LoadingSpinner,
} from "./AdminPanel";
import ImageUpload from "./ImageUpload";
import "../../styles/products.css";

// React Icons imports
import {
  FiSearch,
  FiTrash2,
  FiPlus,
  FiEdit2,
  FiX,
  FiPackage,
  FiImage,
  FiTag,
  FiStar,
  FiFolder,
  FiGrid,
  FiSave,
  FiCopy,
  FiEye,
  FiEyeOff,
  FiAlertCircle,
  FiCheckCircle,
  // FiPalette,
} from "react-icons/fi";
import {
  FaRupeeSign,
  FaRuler,
  FaBoxes,
  FaTags,
  FaDollarSign,
} from "react-icons/fa";
import {
  MdInventory,
  MdDescription,
  MdCategory,
  MdLocalOffer,
} from "react-icons/md";

const DEFAULT_SIZES = [
  { name: "Regular (2.0m)", price: null, stock: 0 },
  { name: "Large (2.25m)", price: null, stock: 0 },
  { name: "XL (2.50m)", price: null, stock: 0 },
];

const DEFAULT_COLORS = [
  { name: "Classic White", hex: "#F5F5F0" },
  { name: "Midnight Blue", hex: "#1B2D5B" },
  { name: "Terracotta", hex: "#C45C3A" },
  { name: "Forest Green", hex: "#2D5A3D" },
  { name: "Saffron", hex: "#E8941A" },
  { name: "Charcoal", hex: "#3D3D3D" },
];

const emptyProduct = {
  name: "",
  brand: "KKB",
  category: "",
  subcategory: "",
  price: "",
  originalPrice: "",
  description: "",
  image: "",
  images: [],
  badge: "",
  inStock: true,
  stock: 100,
  featured: false,
  sizes: JSON.parse(JSON.stringify(DEFAULT_SIZES)),
  colors: JSON.parse(JSON.stringify(DEFAULT_COLORS)),
  features: [],
};

const FALLBACK_CATEGORIES = [
  { _id: "1", name: "Lungies", isActive: true, icon: "👕" },
  { _id: "2", name: "Kaavi", isActive: true, icon: "🎨" },
  { _id: "3", name: "Bedsheets", isActive: true, icon: "🛏️" },
  { _id: "4", name: "Towel", isActive: true, icon: "🧣" },
];

// Color Card Component
function ColorCard({ color, index, onUpdate, onRemove }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className={`color-card-enhanced ${isExpanded ? "expanded" : ""}`}>
      <div className="color-card-header">
        <div className="color-info">
          <div
            className="color-preview"
            style={{ backgroundColor: color.hex || "#888" }}
          />
          <div className="color-details">
            <input
              type="text"
              className="color-name-input"
              value={color.name}
              onChange={(e) => onUpdate("name", e.target.value)}
              placeholder="Color name"
            />
            <div className="color-hex-input-wrapper">
              <span className="hex-hash">#</span>
              <input
                type="text"
                className="color-hex-input"
                value={color.hex?.replace("#", "") || ""}
                onChange={(e) => {
                  let hex = e.target.value;
                  if (!hex.startsWith("#")) hex = "#" + hex;
                  onUpdate("hex", hex);
                }}
                placeholder="HEX code"
                maxLength="7"
              />
            </div>
          </div>
        </div>
        <div className="color-actions">
          <button
            className="color-expand-btn"
            onClick={() => setIsExpanded(!isExpanded)}
            title="Edit details"
          >
            <FiEdit2 size={12} />
          </button>
          <button
            className="color-remove-btn"
            onClick={onRemove}
            title="Remove color"
          >
            <FiX size={14} />
          </button>
        </div>
      </div>
      {isExpanded && (
        <div className="color-card-body">
          <div className="color-fields">
            <div className="color-field">
              <label>Color Preview</label>
              <div
                className="color-preview-large"
                style={{ backgroundColor: color.hex || "#888" }}
              />
            </div>
            <div className="color-field">
              <label>Color Name</label>
              <input
                type="text"
                value={color.name}
                onChange={(e) => onUpdate("name", e.target.value)}
                placeholder="e.g., Midnight Blue"
              />
            </div>
            <div className="color-field">
              <label>HEX Code</label>
              <div className="hex-picker-wrapper">
                <input
                  type="color"
                  value={color.hex || "#888888"}
                  onChange={(e) => onUpdate("hex", e.target.value)}
                  className="color-picker"
                />
                <input
                  type="text"
                  value={color.hex || ""}
                  onChange={(e) => onUpdate("hex", e.target.value)}
                  placeholder="#RRGGBB"
                  className="hex-text-input"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Size Card Component
function SizeCard({ size, index, onUpdate, onRemove, isLast, basePrice }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const finalPrice = size.price || basePrice;

  return (
    <div className={`size-card-enhanced ${isExpanded ? "expanded" : ""}`}>
      <div className="size-card-header">
        <div className="size-info">
          <div className="size-badge">{index + 1}</div>
          <input
            type="text"
            className="size-name-input"
            value={size.name}
            onChange={(e) => onUpdate("name", e.target.value)}
            placeholder="Size name"
          />
        </div>
        <div className="size-stats">
          <span className="size-price">
            <FaRupeeSign size={10} /> {finalPrice?.toLocaleString() || "Base"}
          </span>
          <span className={`size-stock ${size.stock < 10 ? "low" : ""}`}>
            <FaBoxes size={10} /> {size.stock} units
          </span>
          <button
            className="size-expand-btn"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <FiEdit2 size={12} />
          </button>
          <button
            className="size-remove-btn"
            onClick={onRemove}
            disabled={isLast}
          >
            <FiX size={14} />
          </button>
        </div>
      </div>
      {isExpanded && (
        <div className="size-card-body">
          <div className="size-fields">
            <div className="size-field">
              <label>Custom Price (₹)</label>
              <input
                type="number"
                className="size-input"
                value={size.price || ""}
                onChange={(e) =>
                  onUpdate(
                    "price",
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
                placeholder={`Default: ₹${basePrice}`}
              />
              <small>Leave empty to use base price</small>
            </div>
            <div className="size-field">
              <label>Stock Quantity *</label>
              <input
                type="number"
                className="size-input"
                value={size.stock}
                onChange={(e) => onUpdate("stock", Number(e.target.value))}
                min="0"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Product Card for Mobile View
function ProductCardMobile({
  product,
  onEdit,
  onDelete,
  isSelected,
  onSelect,
}) {
  const totalStock = (() => {
    if (!product.sizes) return product.stock || 0;
    let sizes = product.sizes;
    if (typeof sizes === "string") {
      try {
        sizes = JSON.parse(sizes);
      } catch {
        sizes = [];
      }
    }
    if (Array.isArray(sizes)) {
      return sizes.reduce((sum, s) => sum + (Number(s.stock) || 0), 0);
    }
    return product.stock || 0;
  })();

  const isLowStock = totalStock < 10 && totalStock > 0;
  const isOutOfStock = totalStock === 0;

  const normalizeImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("https://lightslategrey-mole-360881.hostingersite.com"))
      return url;
    if (url.startsWith("/uploads"))
      return `https://lightslategrey-mole-360881.hostingersite.com${url}`;
    if (!url.includes("/") && !url.includes("http")) {
      return `https://lightslategrey-mole-360881.hostingersite.com/uploads/products/${url}`;
    }
    return url;
  };

  return (
    <div className="product-card-mobile-enhanced">
      <div className="product-card-checkbox">
        <input type="checkbox" checked={isSelected} onChange={onSelect} />
      </div>
      <div className="product-card-image">
        {product.image ? (
          <img
            src={normalizeImageUrl(product.image)}
            alt={product.name}
            onError={(e) => (e.target.style.display = "none")}
          />
        ) : (
          <div className="image-placeholder">
            <FiImage />
          </div>
        )}
        {product.featured && (
          <div className="featured-badge">
            <FiStar /> Featured
          </div>
        )}
        {product.badge && <div className="product-badge">{product.badge}</div>}
      </div>
      <div className="product-card-info">
        <h4 className="product-card-name">{product.name}</h4>
        <div className="product-card-meta">
          <span className="product-category">
            <MdCategory /> {product.category}
          </span>
          <span className="product-brand">{product.brand}</span>
        </div>
        <div className="product-card-price">
          <span className="current-price">
            ₹{product.price?.toLocaleString()}
          </span>
          {product.originalPrice && (
            <span className="original-price">
              ₹{product.originalPrice?.toLocaleString()}
            </span>
          )}
        </div>
        <div className="product-card-stock">
          <div
            className={`stock-indicator ${isOutOfStock ? "out" : isLowStock ? "low" : "good"}`}
          >
            {isOutOfStock ? (
              <FiAlertCircle />
            ) : isLowStock ? (
              <FiAlertCircle />
            ) : (
              <FiCheckCircle />
            )}
            <span>
              {isOutOfStock
                ? "Out of Stock"
                : isLowStock
                  ? `Low Stock: ${totalStock} left`
                  : `${totalStock} in stock`}
            </span>
          </div>
        </div>
        <div className="product-card-actions">
          <button onClick={() => onEdit(product)} className="card-action edit">
            <FiEdit2 /> Edit
          </button>
          <button
            onClick={() => onDelete(product.id)}
            className="card-action delete"
          >
            <FiTrash2 /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Products({ toast }) {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const debounceRef = useRef();
  const abortControllerRef = useRef(null);

  const normalizeImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("https://lightslategrey-mole-360881.hostingersite.com"))
      return url;
    if (url.startsWith("/uploads"))
      return `https://lightslategrey-mole-360881.hostingersite.com${url}`;
    if (!url.includes("/") && !url.includes("http")) {
      return `https://lightslategrey-mole-360881.hostingersite.com/uploads/products/${url}`;
    }
    return url;
  };

  const loadCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const response = await aGet("/categories");
      if (response && response.categories && response.categories.length > 0) {
        setCategories(response.categories.filter((cat) => cat.isActive));
      }
    } catch (error) {
      console.error("Error loading categories:", error);
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  const load = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    setLoading(true);
    try {
      const response = await aGet("/products", {
        page,
        search,
        category,
        signal: abortControllerRef.current.signal,
      });

      const productsWithId = (response.products || []).map((product) => ({
        ...product,
        _id: product.id,
        id: product.id,
        image: normalizeImageUrl(product.image),
        images: product.images
          ? typeof product.images === "string"
            ? JSON.parse(product.images)
            : product.images
          : [],
        colors: product.colors
          ? typeof product.colors === "string"
            ? JSON.parse(product.colors)
            : product.colors
          : DEFAULT_COLORS,
      }));

      setProducts(productsWithId);
      setTotal(response.total || 0);
      setPages(response.pages || 1);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error loading products:", error);
        toast(error.message || "Failed to load products", "error");
      }
    } finally {
      setLoading(false);
    }
  }, [page, search, category, toast]);

  useEffect(() => {
    loadCategories();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      load();
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [load]);

  const generateSubcategoryFromCategory = (categoryName) => {
    if (!categoryName) return "";
    return categoryName.toLowerCase().replace(/\s+/g, "-") + "-collection";
  };

  const openAdd = () => {
    const defaultCategory = categories[0]?.name || "";
    setForm({
      ...emptyProduct,
      category: defaultCategory,
      subcategory: generateSubcategoryFromCategory(defaultCategory),
      sizes: JSON.parse(JSON.stringify(DEFAULT_SIZES)),
      colors: JSON.parse(JSON.stringify(DEFAULT_COLORS)),
      image: "",
      images: [],
    });
    setModal("add");
  };

  const openEdit = (p) => {
    let sizes = [];
    if (p.sizes && Array.isArray(p.sizes) && p.sizes.length > 0) {
      sizes = p.sizes;
    } else if (typeof p.sizes === "string") {
      try {
        sizes = JSON.parse(p.sizes);
      } catch (e) {
        sizes = JSON.parse(JSON.stringify(DEFAULT_SIZES));
      }
    } else {
      sizes = JSON.parse(JSON.stringify(DEFAULT_SIZES));
    }

    let colors = [];
    if (p.colors && Array.isArray(p.colors) && p.colors.length > 0) {
      colors = p.colors;
    } else if (typeof p.colors === "string") {
      try {
        colors = JSON.parse(p.colors);
      } catch (e) {
        colors = JSON.parse(JSON.stringify(DEFAULT_COLORS));
      }
    } else {
      colors = JSON.parse(JSON.stringify(DEFAULT_COLORS));
    }

    let images = p.images || [];
    if (typeof images === "string") {
      try {
        images = JSON.parse(images);
      } catch {
        images = [];
      }
    }
    if (!Array.isArray(images)) images = [];

    setForm({
      ...p,
      _id: p.id,
      id: p.id,
      price: p.price || "",
      originalPrice: p.originalPrice || "",
      sizes: sizes,
      colors: colors,
      images: images,
      image: normalizeImageUrl(p.image),
      originalPrice: p.comparePrice, // ✅ map comparePrice → originalPrice for the form
      isFeatured: p.isFeatured,
      featured: p.isFeatured,
      subcategory: p.subcategory || generateSubcategoryFromCategory(p.category),
    });
    setModal("edit");
  };

  const handleSizeChange = (index, field, value) => {
    const newSizes = [...form.sizes];
    newSizes[index] = { ...newSizes[index], [field]: value };
    setForm({ ...form, sizes: newSizes });
  };

  const addSize = () => {
    setForm({
      ...form,
      sizes: [...form.sizes, { name: "", price: null, stock: 0 }],
    });
  };

  const removeSize = (index) => {
    if (form.sizes.length === 1) {
      toast("At least one size is required", "error");
      return;
    }
    const newSizes = form.sizes.filter((_, i) => i !== index);
    setForm({ ...form, sizes: newSizes });
  };

  // Color management functions
  const handleColorChange = (index, field, value) => {
    const newColors = [...form.colors];
    newColors[index] = { ...newColors[index], [field]: value };
    setForm({ ...form, colors: newColors });
  };

  const addColor = () => {
    setForm({
      ...form,
      colors: [...form.colors, { name: "", hex: "#888888" }],
    });
  };

  const removeColor = (index) => {
    if (form.colors.length === 1) {
      toast("At least one color option is required", "error");
      return;
    }
    const newColors = form.colors.filter((_, i) => i !== index);
    setForm({ ...form, colors: newColors });
  };

  const handleCategoryChange = (categoryName) => {
    const autoSubcategory = generateSubcategoryFromCategory(categoryName);
    setForm({
      ...form,
      category: categoryName,
      subcategory: autoSubcategory,
    });
  };

  const handleSave = async () => {
    if (!form.name) {
      toast("Product name is required", "error");
      return;
    }
    if (!form.price) {
      toast("Price is required", "error");
      return;
    }
    if (!form.category) {
      toast("Please select a category", "error");
      return;
    }
    if (!form.image) {
      toast("Please upload a product image", "error");
      return;
    }

    for (let i = 0; i < form.sizes.length; i++) {
      if (!form.sizes[i].name) {
        toast(`Size ${i + 1} name is required`, "error");
        return;
      }
    }

    for (let i = 0; i < form.colors.length; i++) {
      if (!form.colors[i].name) {
        toast(`Color ${i + 1} name is required`, "error");
        return;
      }
      if (!form.colors[i].hex) {
        toast(`Color ${i + 1} HEX code is required`, "error");
        return;
      }
    }

    setSaving(true);
    try {
      const totalStock = form.sizes.reduce(
        (sum, size) => sum + (Number(size.stock) || 0),
        0,
      );

      const productData = {
        name: form.name,
        brand: form.brand || "KKB",
        price: Number(form.price),
        comparePrice: form.originalPrice
          ? Number(form.originalPrice)
          : undefined, // ← renamed
        category: form.category,
        subcategory:
          form.subcategory || generateSubcategoryFromCategory(form.category),
        description: form.description || "",
        image: form.image,
        images: JSON.stringify(Array.isArray(form.images) ? form.images : []),
        badge: form.badge || null,
        inStock: totalStock > 0,
        stock: totalStock,
        isFeatured: form.featured || false, // ← renamed
        sizes: JSON.stringify(
          form.sizes.map((s) => ({
            name: s.name,
            price: s.price ? Number(s.price) : null,
            stock: Number(s.stock) || 0,
          })),
        ),
        colors: JSON.stringify(
          form.colors.map((c) => ({
            name: c.name,
            hex: c.hex,
          })),
        ),
      };

      if (modal === "add") {
        await aPost("/products", productData);
        toast("Product created successfully ✓");
      } else {
        const productId = form.id || form._id;
        if (!productId) throw new Error("Product ID is missing");
        await aPut(`/products/${productId}`, productData);
        toast("Product updated successfully ✓");
      }
      setModal(null);
      load();
    } catch (e) {
      console.error("Save error:", e);
      toast(e.message || "Failed to save product", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await aDelete(`/products/${id}`);
      toast("Product deleted");
      setSelected((prev) => prev.filter((item) => item !== id));
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} products?`)) return;
    try {
      await aPost("/products/bulk-delete", { ids: selected });
      toast(`${selected.length} products deleted`);
      setSelected([]);
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const calculateTotalStock = () => {
    return form.sizes.reduce((sum, size) => sum + (Number(size.stock) || 0), 0);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSelected([]);
  };

  const toggleSelectAll = () => {
    if (selected.length === products.length) {
      setSelected([]);
    } else {
      setSelected(products.map((p) => p.id));
    }
  };

  const getCategoryIcon = (catName) => {
    const cat = categories.find((c) => c.name === catName);
    return cat?.icon || "📦";
  };

  const getTotalStockForProduct = (product) => {
    if (!product.sizes) return product.stock || 0;
    let sizes = product.sizes;
    if (typeof sizes === "string") {
      try {
        sizes = JSON.parse(sizes);
      } catch {
        return 0;
      }
    }
    if (Array.isArray(sizes)) {
      return sizes.reduce((sum, s) => sum + (Number(s.stock) || 0), 0);
    }
    return product.stock || 0;
  };

  return (
    <div className="products-container-enhanced">
      {/* Header */}
      <div className="products-header-enhanced">
        <div>
          <p className="products-subtitle">
            Manage your product catalog, inventory, and pricing
          </p>
        </div>
        <div className="products-stats">
          <div className="stat-item">
            <FiPackage />
            <span>{total} Products</span>
          </div>
          <div className="stat-item">
            <MdCategory />
            <span>{categories.length} Categories</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="products-toolbar-enhanced">
        <div className="toolbar-left">
          <div className="search-wrapper">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, category, or brand..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="search-input"
            />
            {search && (
              <button onClick={() => setSearch("")} className="clear-search">
                <FiX />
              </button>
            )}
          </div>
          <button
            className={`filter-toggle ${showFilters ? "active" : ""}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FiGrid /> Filter
          </button>
        </div>
        <div className="toolbar-right">
          {selected.length > 0 && (
            <button className="bulk-delete-btn" onClick={handleBulkDelete}>
              <FiTrash2 /> Delete ({selected.length})
            </button>
          )}
          <button className="add-product-btn" onClick={openAdd}>
            <FiPlus /> Add Product
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel-enhanced">
          <div className="filter-group">
            <label>Category</label>
            <select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c._id} value={c.name}>
                  {c.icon} {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-actions">
            <button
              onClick={() => {
                setCategory("");
                setSearch("");
              }}
              className="clear-filters"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Desktop Table View */}
      <div className="products-table-wrapper-enhanced">
        <Table
          loading={loading}
          columns={[
            {
              key: "id",
              label: "",
              width: "40px",
              render: (v) => (
                <input
                  type="checkbox"
                  checked={selected.includes(v)}
                  onChange={() =>
                    setSelected((prev) =>
                      prev.includes(v)
                        ? prev.filter((x) => x !== v)
                        : [...prev, v],
                    )
                  }
                  className="product-checkbox"
                />
              ),
            },
            {
              key: "image",
              render: (v, row) =>
                v ? (
                  <img
                    src={normalizeImageUrl(v)} // ✅ add this
                    alt={row.name}
                    className="product-thumbnail"
                    onError={(e) => (e.target.style.display = "none")}
                  />
                ) : (
                  <div className="thumbnail-placeholder">
                    <FiImage />
                  </div>
                ),
            },
            ,
            {
              key: "name",
              label: "Product",
              render: (v, row) => (
                <div className="product-name-cell">
                  <span className="product-name">{v}</span>
                  <span className="product-brand">{row.brand}</span>
                </div>
              ),
            },
            {
              key: "category",
              label: "Category",
              render: (v) => (
                <div className="category-cell">
                  <span className="category-icon">{getCategoryIcon(v)}</span>
                  <span>{v}</span>
                </div>
              ),
            },
            {
              key: "price",
              label: "Price",
              render: (v, row) => (
                <div className="price-cell">
                  <span className="current-price">₹{v?.toLocaleString()}</span>
                  {row.originalPrice && (
                    <span className="original-price">
                      ₹{row.originalPrice?.toLocaleString()}
                    </span>
                  )}
                </div>
              ),
            },
            {
              key: "stock",
              label: "Stock",
              render: (_, row) => {
                const totalStock = getTotalStockForProduct(row);
                const isLow = totalStock < 10 && totalStock > 0;
                const isOut = totalStock === 0;
                return (
                  <div
                    className={`stock-cell ${isOut ? "out" : isLow ? "low" : "good"}`}
                  >
                    {isOut ? (
                      <FiAlertCircle />
                    ) : isLow ? (
                      <FiAlertCircle />
                    ) : (
                      <FiCheckCircle />
                    )}
                    <span>{totalStock} units</span>
                  </div>
                );
              },
            },
            {
              key: "featured",
              key: "isFeatured",
              render: (v) =>
                v ? (
                  <FiStar className="featured-star" />
                ) : (
                  <span className="dash">—</span>
                ),
            },
            {
              key: "badge",
              label: "Badge",
              render: (v) =>
                v ? <span className="product-badge-table">{v}</span> : null,
            },
            {
              key: "id",
              label: "Actions",
              width: "100px",
              render: (v, row) => (
                <div className="action-buttons-cell">
                  <button
                    className="icon-btn edit"
                    onClick={() => openEdit(row)}
                    title="Edit"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    className="icon-btn delete"
                    onClick={() => handleDelete(v)}
                    title="Delete"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              ),
            },
          ]}
          data={products}
          rowKey="id"
        />
      </div>

      {/* Mobile Card View */}
      {!loading && products.length > 0 && (
        <div className="products-mobile-view">
          {products.map((product) => (
            <ProductCardMobile
              key={product.id}
              product={product}
              onEdit={openEdit}
              onDelete={handleDelete}
              isSelected={selected.includes(product.id)}
              onSelect={() => {
                setSelected((prev) =>
                  prev.includes(product.id)
                    ? prev.filter((x) => x !== product.id)
                    : [...prev, product.id],
                );
              }}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && products.length === 0 && (
        <EmptyState
          icon={FiPackage}
          title="No products found"
          description="Get started by adding your first product"
          action={{ label: "Add Product", onClick: openAdd }}
        />
      )}

      {/* Loading State */}
      {loading && (
        <div className="products-loading">
          <LoadingSpinner size="lg" text="Loading products..." />
        </div>
      )}

      {/* Pagination */}
      {!loading && products.length > 0 && (
        <Pagination page={page} pages={pages} onChange={handlePageChange} />
      )}

      {/* Add/Edit Modal */}
      {modal && (
        <Modal
          title={modal === "add" ? "Create New Product" : "Edit Product"}
          onClose={() => setModal(null)}
          size="lg"
        >
          <div className="product-modal-enhanced">
            <div className="modal-layout">
              {/* Left Column - Images */}
              <div className="modal-left">
                <div className="image-section">
                  <h4>Product Images</h4>
                  <div className="main-image-upload">
                    <ImageUpload
                      currentImage={form.image}
                      onImageUploaded={(imageUrl) => setForm({ ...form, image: imageUrl })}
                      onImageRemoved={() => setForm({ ...form, image: "" })}
                      label="Main Product Image *"
                      productId={form.id || form._id}   // ← add this
                    />
                  </div>
                  <div className="additional-images">
                    <ImageUpload
                      currentImage={null}
                      onMultipleImagesUploaded={(images) => {
                        const imagesArray = Array.isArray(images) ? images : [images];
                        const currentImages = Array.isArray(form.images) ? form.images : [];
                        setForm({ ...form, images: [...currentImages, ...imagesArray] });
                      }}
                      label="Additional Images"
                      multiple={true}
                      existingImages={Array.isArray(form.images) ? form.images : []}
                      productId={form.id || form._id || null}  // ← add this
                    />
                    {Array.isArray(form.images) && form.images.length > 0 && (
                      <div className="thumbnails-grid">
                        {form.images.map((img, idx) => (
                          <div key={idx} className="thumbnail-item">
                            <img
                              src={normalizeImageUrl(img)}
                              alt={`Thumb ${idx + 1}`}
                            />
                            <button
                              onClick={async () => {
                                try {
                                  // Extract just the relative path, not the full URL
                                  let imageUrl = img;
                                  if (img.includes("/uploads/")) {
                                    imageUrl = img.split("/uploads/").pop(); // e.g. "products/product-123.jpg"
                                  }

                                  await aDelete("/upload/image", {
                                    imageUrl,
                                    productId: form.id || form._id || null,
                                    field: "images",
                                  });

                                  const newImages = form.images.filter((_, i) => i !== idx);
                                  setForm({ ...form, images: newImages });
                                  toast("Image removed successfully");
                                } catch (error) {
                                  toast(error.message, "error");
                                }
                              }}
                              className="remove-thumb"
                            >
                              <FiX />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Product Details */}
              <div className="modal-right">
                <div className="details-section">
                  <h4>Basic Information</h4>
                  <div className="form-grid">
                    <div className="form-field">
                      <label>Product Name *</label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) =>
                          setForm({ ...form, name: e.target.value })
                        }
                        placeholder="Enter product name"
                      />
                    </div>
                    <div className="form-field">
                      <label>Brand</label>
                      <input
                        type="text"
                        value={form.brand}
                        onChange={(e) =>
                          setForm({ ...form, brand: e.target.value })
                        }
                        placeholder="Brand name"
                      />
                    </div>
                    <div className="form-field">
                      <label>Category *</label>
                      <select
                        value={form.category}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                      >
                        <option value="">Select category</option>
                        {categories.map((c) => (
                          <option key={c._id} value={c.name}>
                            {c.icon} {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-field">
                      <label>Subcategory</label>
                      <input
                        type="text"
                        value={form.subcategory}
                        onChange={(e) =>
                          setForm({ ...form, subcategory: e.target.value })
                        }
                        placeholder="Auto-generated"
                      />
                    </div>
                    <div className="form-field">
                      <label>Base Price (₹) *</label>
                      <input
                        type="number"
                        value={form.price}
                        onChange={(e) =>
                          setForm({ ...form, price: e.target.value })
                        }
                        placeholder="Base price"
                      />
                    </div>
                    <div className="form-field">
                      <label>Original Price (₹)</label>
                      <input
                        type="number"
                        value={form.originalPrice || ""}
                        onChange={(e) =>
                          setForm({ ...form, originalPrice: e.target.value })
                        }
                        placeholder="For discount display"
                      />
                    </div>
                    <div className="form-field">
                      <label>Badge</label>
                      <input
                        type="text"
                        value={form.badge || ""}
                        onChange={(e) =>
                          setForm({ ...form, badge: e.target.value })
                        }
                        placeholder="NEW, HOT, SALE"
                      />
                    </div>
                    <div className="form-field checkbox-field">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={form.featured}
                          onChange={(e) =>
                            setForm({ ...form, featured: e.target.checked })
                          }
                        />
                        <FiStar /> Featured Product
                      </label>
                    </div>
                  </div>

                  <div className="description-field">
                    <label>Description</label>
                    <textarea
                      value={form.description || ""}
                      onChange={(e) =>
                        setForm({ ...form, description: e.target.value })
                      }
                      rows={4}
                      placeholder="Product description..."
                    />
                  </div>

                  {/* Colors Section */}
                  <h4>
                    {/* <FiPalette /> Colors & Variants */}
                    Colors & Variants
                  </h4>
                  <div className="colors-section-enhanced">
                    {form.colors.map((color, idx) => (
                      <ColorCard
                        key={idx}
                        color={color}
                        index={idx}
                        onUpdate={(field, value) =>
                          handleColorChange(idx, field, value)
                        }
                        onRemove={() => removeColor(idx)}
                      />
                    ))}
                    <button className="add-color-btn" onClick={addColor}>
                      <FiPlus /> Add Color Option
                    </button>
                    <div className="color-info-note">
                      <FiAlertCircle size={14} />
                      <small>
                        These colors will be displayed on the product page for
                        customers to choose from
                      </small>
                    </div>
                  </div>

                  {/* Sizes Section */}
                  <h4>
                    <FaRuler /> Sizes & Inventory
                  </h4>
                  <div className="sizes-section-enhanced">
                    {form.sizes.map((size, idx) => (
                      <SizeCard
                        key={idx}
                        size={size}
                        index={idx}
                        onUpdate={(field, value) =>
                          handleSizeChange(idx, field, value)
                        }
                        onRemove={() => removeSize(idx)}
                        isLast={form.sizes.length === 1}
                        basePrice={form.price}
                      />
                    ))}
                    <button className="add-size-btn" onClick={addSize}>
                      <FiPlus /> Add Size Variant
                    </button>
                    <div className="total-stock-info">
                      <MdInventory />
                      <span>
                        Total Inventory:{" "}
                        <strong>{calculateTotalStock()} units</strong>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-actions-enhanced">
              <button className="cancel-btn" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button
                className="save-btn"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <FiSave className="spin" /> Saving...
                  </>
                ) : modal === "add" ? (
                  "Create Product"
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
